'use client';

import { LngLat, SuggestResponseItem } from '@yandex/ymaps3-types';
import { clusterByGrid as clusterByGridMethod } from '@yandex/ymaps3-types/packages/clusterer';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMaps } from '@/api/yandex-maps';
import { IconKitCloseMap } from '@/icon/kit/CloseMap';
import { IconKitSearchMap } from '@/icon/kit/SearchMap';
import { UiPickupPointInfo } from '@/ui/pickup-point-info';
import { UiPickupPointSelector } from '@/ui/pickup-point-selector';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitDrawer } from '@/ui-kit/drawer';
import { MapBoundsChangePayload, UiKitMapContent } from '@/ui-kit/map-content';
import { UiKitPanel, UiKitPanelProps } from '@/ui-kit/panel';
import { UiKitSearchLocationField } from '@/ui-kit/search-location-field';
import { useMediaQuery } from '@/ui-tool/media-query';
import { DEFAULT_CLUSTER_GRID_SIZE } from '@/util/map-point-maps/consts';
import { useMapController, usePointsController } from '@/util/map-point-maps/hooks';
import {
  PickupPoint,
  PickupPointTypeOption,
  PointFeature
} from '@/util/map-point-maps/types';
import { getAddressFromSuggest } from '@/util/map-point-maps/utils/getAddressFromSuggest';

import { styles } from './UiMapsPickup.styles';

interface DefaultProps {
  points: PickupPoint[];
  variant?: 'default';
}

export type UiMapsPickupProps = DefaultProps & {
  onPointConfirm?: (pointId: string) => void;
  city?: string;
  /**
   * Смещение поля поиска относительно правого края экрана
   * Может использоваться для диалоговых окон где справа будет кнопка закрытия
   * Указывается в пикселях
   */
  searchFieldRightOffset?: number;
  /**
   * Флаг для отображения только карты на мобильных устройствах
   */
  isMobileOnlyMap?: boolean;
  /**
   * Заголовок для селектора точек
   */
  pointSelectorTitle?: string;
  /**
   * Флаг для отображения селектора точек, по умолчанию true
   */
  isHasPointTypeSelector?: boolean;

  center?: LngLat;

  hideSearchField?: boolean;

  pickupPointSelectorSize?: 'small' | 'default';

  tabsView?: UiKitPanelProps['view'];

  loading?: boolean;

  isFreeShipping?: boolean;

  pointTypesOptions?: PickupPointTypeOption[];

  onBoundsChange?: (bounds: MapBoundsChangePayload) => void;
};

export function UiMapsPickup({
  onPointConfirm,
  city,
  searchFieldRightOffset,
  isMobileOnlyMap = false,
  pointSelectorTitle,
  isHasPointTypeSelector = true,
  center,
  hideSearchField = false,
  pickupPointSelectorSize = 'default',
  tabsView = 'tab',
  loading = false,
  isFreeShipping,
  pointTypesOptions: pointTypesOptionsProp,
  onBoundsChange,
  ...props
}: UiMapsPickupProps) {
  const slots = styles();

  const t = useTranslations();

  const { reactifyApi, clusterer, suggest, search } = useMaps();
  const [isIgnoringBoundsChange, setIsIgnoringBoundsChange] = useState(false);
  const ignoreBoundsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (ignoreBoundsTimeoutRef.current) clearTimeout(ignoreBoundsTimeoutRef.current);
    };
  }, []);

  const startIgnoringBoundsChange = useCallback(() => {
    setIsIgnoringBoundsChange(true);
    if (ignoreBoundsTimeoutRef.current) clearTimeout(ignoreBoundsTimeoutRef.current);
    ignoreBoundsTimeoutRef.current = setTimeout(() => {
      ignoreBoundsTimeoutRef.current = null;
      setIsIgnoringBoundsChange(false);
    }, 2000);
  }, []);

  const {
    location,
    selectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleLocationSelect,
    handleZoomIn,
    handleZoomOut
  } = useMapController(center);

  const { visiblePoints, selectedPointType, handlePointTypeChange, pointTypesOptions } =
    usePointsController(props.points, {
      pointTypesOptions: pointTypesOptionsProp
    });

  const pointsList = visiblePoints.flatMap(({ points }) => points);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [isSearchLocationField, isSearchLocationFieldSet] = useState(true);

  const isDesktop = useMediaQuery('lg');

  const gridSizedMethod = useMemo(() => {
    if (!clusterer) {
      return null;
    }

    const clusterByGrid =
      clusterer.clusterByGrid as unknown as typeof clusterByGridMethod;

    return clusterByGrid({ gridSize: DEFAULT_CLUSTER_GRID_SIZE });
  }, [clusterer]);

  const handleSuggestSelect = async (option: SuggestResponseItem | null) => {
    if (!search || !option) {
      return;
    }

    const result = await search({
      text: getAddressFromSuggest(option)
    });

    const firstResult = result?.[0];

    const coordinates = firstResult?.geometry?.coordinates;

    if (coordinates) {
      handleLocationSelect(coordinates as [number, number]);
    }
  };

  const handlePointConfirm = (pointId: string) => {
    onPointConfirm?.(pointId);
  };

  const onMapMarkerClickWithDrawer = useCallback(
    (feature: PointFeature) => {
      startIgnoringBoundsChange();
      const result = handleMarkerClick(true)(feature);

      if (!isDesktop && result.isMap) {
        setOpenDrawer(true);
      }
    },
    [handleMarkerClick, isDesktop, startIgnoringBoundsChange]
  );

  if (!reactifyApi || !clusterer || !gridSizedMethod || !suggest) {
    return null;
  }

  const pickupPointSelector = (
    <UiPickupPointSelector
      size={pickupPointSelectorSize}
      key="pickup-point-selector"
      onPointConfirm={handlePointConfirm}
      selectedPointType={selectedPointType}
      activePointId={selectedPoint?.id ?? null}
      pointsList={pointsList}
      pointTypesOptions={pointTypesOptions}
      onSelectPoint={(feature) => {
        startIgnoringBoundsChange();
        return handleMarkerClick(false)(feature);
      }}
      onPointTypeChange={handlePointTypeChange}
      pointSelectorTitle={pointSelectorTitle}
      isHasPointTypeSelector={isHasPointTypeSelector}
      hideConfirmButton={!onPointConfirm}
      loading={loading}
      isFreeShipping={isFreeShipping}
    />
  );

  const pickupPointSelectorOnly = (
    <div className={slots.pickupPointSelectorOnly()}>
      <UiPickupPointSelector
        size={pickupPointSelectorSize}
        key="pickup-point-selector"
        onPointConfirm={handlePointConfirm}
        selectedPointType={selectedPointType}
        activePointId={selectedPoint?.id ?? null}
        pointTypesOptions={pointTypesOptions}
        onSelectPoint={handleMarkerClick(false)}
        onPointTypeChange={handlePointTypeChange}
        pointSelectorTitle={pointSelectorTitle}
        isHasPointTypeSelector={isHasPointTypeSelector}
        hideConfirmButton={!onPointConfirm}
        loading={loading}
        isFreeShipping={isFreeShipping}
      />
    </div>
  );

  const searchLocationField = hideSearchField ? null : (
    <>
      <UiKitButton
        color="transparent"
        className={slots.searchLocationFieldBtn({
          mobileView: !isDesktop,
          mobileViewField: !isDesktop && isSearchLocationField ? true : false
        })}
        onClick={() => isSearchLocationFieldSet(!isSearchLocationField)}
      >
        {isSearchLocationField ? (
          <IconKitSearchMap
            width={48}
            height={48}
          />
        ) : (
          <IconKitCloseMap
            width={48}
            height={48}
          />
        )}
      </UiKitButton>
      <div
        className={slots.searchLocationField({
          mobileView: !isDesktop,
          mobileViewField: !isDesktop && isSearchLocationField ? true : false
        })}
        style={
          {
            ...(!!searchFieldRightOffset && {
              '--pickup-additional-offset': `${searchFieldRightOffset}px`
            })
          } as React.CSSProperties
        }
      >
        <UiKitSearchLocationField
          suggest={suggest}
          onSelect={handleSuggestSelect}
          city={city}
        />
      </div>
    </>
  );

  const mapNode = (
    <div className={slots.mapWrapper()}>
      {loading && (
        <div
          className={slots.mapOverlay()}
          aria-hidden
        />
      )}
      {!isDesktop && searchLocationField}
      <UiKitMapContent
        key="map-content"
        reactifyApi={reactifyApi}
        clusterer={clusterer}
        gridSizedMethod={gridSizedMethod}
        pointsList={pointsList}
        selectedPoint={selectedPoint}
        location={location}
        isDesktop={isDesktop}
        onMarkerClick={(feature) => onMapMarkerClickWithDrawer(feature as PointFeature)}
        onClusterClick={handleClusterClick}
        onBoundsChange={isIgnoringBoundsChange ? () => {} : onBoundsChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <div className={slots.base()}>
        {isDesktop && (
          <div
            className={slots.pickupPointSelector({
              size: pickupPointSelectorSize
            })}
          >
            {pickupPointSelector}
          </div>
        )}
        {searchLocationField}
        {mapNode}
      </div>
    );
  }

  const mobileMapNode = (
    <div className={slots.panelContent()}>
      {pickupPointSelectorOnly}
      {mapNode}
    </div>
  );

  return (
    <>
      <UiKitDrawer
        title=""
        open={openDrawer && !!selectedPoint}
        onOpenChange={setOpenDrawer}
        direction="bottom"
        rounded="top"
      >
        {selectedPoint && (
          <UiPickupPointInfo
            point={selectedPoint as PointFeature}
            activePointId={selectedPoint?.id ?? null}
            onPointConfirm={handlePointConfirm}
            hideConfirmButton={!onPointConfirm}
            isFreeShipping={isFreeShipping}
          />
        )}
      </UiKitDrawer>
      {isMobileOnlyMap ? (
        mobileMapNode
      ) : (
        <UiKitPanel
          tabsClassName={slots.tabs()}
          tabClassName={slots.tab()}
          view={tabsView}
          items={[
            {
              header: t('asList'),
              content: <div className={slots.panelContent()}>{pickupPointSelector}</div>
            },
            {
              header: t('asMap'),
              content: <div className={slots.panelContent()}>{mobileMapNode}</div>
            }
          ]}
        />
      )}
    </>
  );
}
